import React, { useMemo, useState } from 'react';
import Select from 'react-select';

/**
 * Generic inline-editable list used by the superadmin Settings page.
 *
 * The component owns only UI state (which row is being edited, the add row, search, the
 * "show inactive" toggle). Persistence is delegated to the callbacks, which are expected to
 * return a promise and THROW on failure — the message is shown inline and the row stays in
 * edit mode so nothing typed is lost.
 *
 * There is deliberately no delete action. Entries managed here (cost centers, order clients)
 * are referenced by historical bookings and credit notes, so the only "removal" is
 * deactivation, which hides an entry from dropdowns while keeping old data intact.
 *
 * Column shape:
 *   { key, label, type: 'text' | 'number' | 'select' | 'readonly', required?, placeholder?,
 *     options? ([{ value, label }] for select), width?, render?(row), addHint? }
 * `addHint` is shown in the add row for readonly columns (e.g. "generated from the label").
 */

// Copy of the black-square react-select look used by the order forms (TypedOrderForm.js),
// compacted for use inside a table row: no bottom margin and a lower control height.
const selectStyles = {
  dropdownIndicator: (provided) => ({ ...provided, border: 'none', padding: '4px' }),
  control: (base) => ({
    ...base,
    width: '100%',
    borderColor: 'black',
    borderWidth: '1px',
    boxShadow: 'none',
    margin: 0,
    borderRadius: '0px',
    color: 'black',
    fontWeight: '700',
    minHeight: '38px',
    textTransform: 'uppercase',
    fontSize: '12px',
  }),
  valueContainer: (provided) => ({ ...provided, padding: '0 8px' }),
  indicatorSeparator: () => ({ display: 'none' }),
  placeholder: (provided) => ({ ...provided, color: 'black', fontWeight: '700' }),
  input: (provided) => ({ ...provided, color: 'black', fontWeight: '700' }),
  menu: (provided) => ({ ...provided, borderRadius: 0, zIndex: 5 }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '12px',
    backgroundColor: state.isSelected ? 'black' : 'inherit',
    '&:hover': { backgroundColor: state.isSelected ? 'black' : '#f7f7f7' },
  }),
};

const isEditable = (col) => col.type !== 'readonly';

/** Label of the select option whose value matches (values are compared as strings: ids may be numbers or strings). */
const optionLabel = (col, value) => {
  const opt = (col.options || []).find((o) => String(o.value) === String(value));
  return opt ? opt.label : '';
};

const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const errorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || (typeof err === 'string' ? err : fallback);

/** Initial values for the edit/add inputs: strings for inputs, raw value for selects. */
const valuesFromRow = (columns, row) =>
  columns.filter(isEditable).reduce((acc, col) => {
    const v = row ? row[col.key] : undefined;
    acc[col.key] = v === null || v === undefined ? '' : v;
    return acc;
  }, {});

/** Client-side check of `required` columns before the API is called. */
const missingRequired = (columns, values) =>
  columns.filter((c) => isEditable(c) && c.required && String(values[c.key] ?? '').trim() === '');

function EditableList({
  id = 'list',
  title,
  description,
  columns,
  rows,
  loading,
  error,
  onCreate,
  onUpdate,
  onDeactivate,
  onReactivate,
  addLabel = 'Add',
  emptyText = 'Nothing here yet.',
}) {
  const listId = slugify(id || title) || 'list';

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Edit mode — one row at a time
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [editError, setEditError] = useState(null);

  // Add row
  const [adding, setAdding] = useState(false);
  const [addValues, setAddValues] = useState({});
  const [addError, setAddError] = useState(null);

  const [saving, setSaving] = useState(false);
  // Errors from Deactivate / Reactivate, keyed by row id
  const [rowErrors, setRowErrors] = useState({});

  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const activeCount = safeRows.filter((r) => r.isActive !== false).length;
  const inactiveCount = safeRows.length - activeCount;

  // Search matches every displayed cell: raw string values plus select option labels and
  // readonly render output when it is a string.
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return safeRows.filter((row) => {
      if (!showInactive && row.isActive === false) return false;
      if (!q) return true;
      return columns.some((col) => {
        let text;
        if (col.type === 'select') text = optionLabel(col, row[col.key]);
        else if (col.render) {
          const out = col.render(row);
          text = typeof out === 'string' || typeof out === 'number' ? String(out) : row[col.key];
        } else text = row[col.key];
        return text !== null && text !== undefined && String(text).toLowerCase().includes(q);
      });
    });
  }, [safeRows, columns, search, showInactive]);

  // ── actions ─────────────────────────────────────────────────────────────────

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditValues(valuesFromRow(columns, row));
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
    setEditError(null);
  };

  const saveEdit = async (row) => {
    const missing = missingRequired(columns, editValues);
    if (missing.length) {
      setEditError(`${missing.map((c) => c.label).join(', ')} ${missing.length > 1 ? 'are' : 'is'} required`);
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      const result = await onUpdate(row, editValues);
      // Callbacks may also report a failure by resolving with a string instead of throwing.
      if (typeof result === 'string' && result) throw new Error(result);
      cancelEdit();
    } catch (err) {
      setEditError(errorMessage(err, 'Could not save the changes'));
    } finally {
      setSaving(false);
    }
  };

  const startAdd = () => {
    setAdding(true);
    setAddValues(valuesFromRow(columns, null));
    setAddError(null);
  };

  const cancelAdd = () => {
    setAdding(false);
    setAddValues({});
    setAddError(null);
  };

  const saveAdd = async () => {
    const missing = missingRequired(columns, addValues);
    if (missing.length) {
      setAddError(`${missing.map((c) => c.label).join(', ')} ${missing.length > 1 ? 'are' : 'is'} required`);
      return;
    }
    setSaving(true);
    setAddError(null);
    try {
      const result = await onCreate(addValues);
      if (typeof result === 'string' && result) throw new Error(result);
      cancelAdd();
    } catch (err) {
      setAddError(errorMessage(err, 'Could not create the entry'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    const reactivating = row.isActive === false;
    setRowErrors((e) => ({ ...e, [row.id]: null }));
    setSaving(true);
    try {
      if (reactivating) await onReactivate(row);
      else await onDeactivate(row);
    } catch (err) {
      setRowErrors((e) => ({ ...e, [row.id]: errorMessage(err, reactivating ? 'Could not reactivate' : 'Could not deactivate') }));
    } finally {
      setSaving(false);
    }
  };

  // Enter saves, Escape cancels — shared by the edit row and the add row.
  const keyHandler = (onSave, onCancel) => (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  // ── rendering ───────────────────────────────────────────────────────────────

  const renderInput = (col, values, setValues, controlId, onKeyDown) => {
    const value = values[col.key] ?? '';
    if (col.type === 'select') {
      const options = col.options || [];
      const selected = options.find((o) => String(o.value) === String(value)) || null;
      return (
        <Select
          inputId={controlId}
          name={controlId}
          options={options}
          value={selected}
          onChange={(opt) => setValues((v) => ({ ...v, [col.key]: opt ? opt.value : '' }))}
          styles={selectStyles}
          placeholder={col.placeholder || '—'}
          isClearable={false}
          menuPlacement="auto"
        />
      );
    }
    return (
      <input
        id={controlId}
        name={controlId}
        className="inputtext editable-list-input"
        type={col.type === 'number' ? 'number' : 'text'}
        value={value}
        placeholder={col.placeholder || ''}
        required={!!col.required}
        onChange={(e) => setValues((v) => ({ ...v, [col.key]: e.target.value }))}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
    );
  };

  const renderCell = (col, row) => {
    if (col.render) return col.render(row);
    if (col.type === 'select') return optionLabel(col, row[col.key]) || <span className="editable-list-muted">—</span>;
    const v = row[col.key];
    return v === null || v === undefined || v === '' ? <span className="editable-list-muted">—</span> : String(v);
  };

  const colStyle = (col) => (col.width ? { width: col.width } : undefined);

  return (
    <div className="editable-list" id={`${listId}-list`}>
      <div className="editable-list-head">
        <div>
          {title && <h2 className="editable-list-title">{title}</h2>}
          {description && <p className="editable-list-description">{description}</p>}
        </div>
        <div className="editable-list-count" id={`${listId}-count`}>
          {activeCount} active · {inactiveCount} inactive
        </div>
      </div>

      <div className="editable-list-toolbar">
        <input
          id={`${listId}-search`}
          name={`${listId}-search`}
          className="inputtext editable-list-search"
          type="search"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="editable-list-toggle" htmlFor={`${listId}-show-inactive`}>
          <input
            id={`${listId}-show-inactive`}
            name={`${listId}-show-inactive`}
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <span>Show inactive</span>
        </label>
        <button
          type="button"
          id={`${listId}-add`}
          className="settings-btn settings-btn-primary"
          onClick={startAdd}
          disabled={adding || loading}
        >
          {addLabel}
        </button>
      </div>

      {error && <div className="editable-list-error" id={`${listId}-error`}>{error}</div>}

      <div className="editable-list-scroll">
        <table className="editable-list-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} style={colStyle(col)}>{col.label}</th>
              ))}
              <th className="editable-list-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <tr className="editable-list-row editing adding" id={`${listId}-add-row`}>
                {columns.map((col) => (
                  <td key={col.key} style={colStyle(col)}>
                    {isEditable(col)
                      ? renderInput(col, addValues, setAddValues, `${listId}-new-${col.key}`, keyHandler(saveAdd, cancelAdd))
                      : <span className="editable-list-hint">{col.addHint || ''}</span>}
                  </td>
                ))}
                <td className="editable-list-actions">
                  <button type="button" id={`${listId}-new-save`} className="settings-btn settings-btn-primary" onClick={saveAdd} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" id={`${listId}-new-cancel`} className="settings-btn" onClick={cancelAdd} disabled={saving}>
                    Cancel
                  </button>
                  {addError && <div className="editable-list-inline-error">{addError}</div>}
                </td>
              </tr>
            )}

            {loading && (
              <tr><td colSpan={columns.length + 1} className="editable-list-empty">Loading…</td></tr>
            )}

            {!loading && visibleRows.length === 0 && !adding && (
              <tr>
                <td colSpan={columns.length + 1} className="editable-list-empty">
                  {safeRows.length === 0 ? emptyText : 'No entries match.'}
                </td>
              </tr>
            )}

            {!loading && visibleRows.map((row) => {
              const inactive = row.isActive === false;
              const editing = editingId === row.id;
              const rowId = `${listId}-row-${row.id}`;
              const rowClass = ['editable-list-row', inactive ? 'inactive' : '', editing ? 'editing' : ''].filter(Boolean).join(' ');

              return (
                <tr key={row.id} id={rowId} className={rowClass}>
                  {columns.map((col, i) => (
                    <td key={col.key} style={colStyle(col)}>
                      {editing && isEditable(col)
                        ? renderInput(col, editValues, setEditValues, `${rowId}-${col.key}`, keyHandler(() => saveEdit(row), cancelEdit))
                        : renderCell(col, row)}
                      {i === 0 && inactive && <span className="editable-list-tag">inactive</span>}
                    </td>
                  ))}
                  <td className="editable-list-actions">
                    {editing ? (
                      <>
                        <button type="button" id={`${rowId}-save`} className="settings-btn settings-btn-primary" onClick={() => saveEdit(row)} disabled={saving}>
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button type="button" id={`${rowId}-cancel`} className="settings-btn" onClick={cancelEdit} disabled={saving}>
                          Cancel
                        </button>
                        {editError && <div className="editable-list-inline-error">{editError}</div>}
                      </>
                    ) : (
                      <>
                        <button type="button" id={`${rowId}-edit`} className="settings-btn" onClick={() => startEdit(row)} disabled={saving || editingId !== null}>
                          Edit
                        </button>
                        {inactive ? (
                          <button type="button" id={`${rowId}-reactivate`} className="settings-btn settings-btn-primary" onClick={() => toggleActive(row)} disabled={saving}>
                            Reactivate
                          </button>
                        ) : (
                          <button type="button" id={`${rowId}-deactivate`} className="settings-btn" onClick={() => toggleActive(row)} disabled={saving}
                            title="Hides the entry from dropdowns. Existing bookings keep it. Can be undone.">
                            Deactivate
                          </button>
                        )}
                        {rowErrors[row.id] && <div className="editable-list-inline-error">{rowErrors[row.id]}</div>}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default EditableList;
